import React, { useState, useContext, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import toast from "react-hot-toast";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import api from "services/api";
import moment from "moment";

import { seasonOptions } from "utils/data";
import { DatePicker } from "antd";
import Error404 from "pages/error/404";

const Color = require("color");

const ColorDetail = () => {
  const [newColor, setNewColor] = useState("");
  const [color, setColor] = useState({});
  const [pageNotFound, setPageNotFound] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const params = useParams();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    getValues,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (params?.id) {
      retrieveColor();
    }
  }, [params?.id]);

  useEffect(() => {
    if (color.id) {
      setNewColor(color?.hex);
      let colorObj = undefined;
      try {
        colorObj = Color(color?.hex);
      } catch (err) {}

      let seasonOpt = seasonOptions.find((x) => x.value == color?.season) || {};
      if (color.season && !seasonOpt.value) {
        seasonOpt = { label: color.season, value: color.season };
      }

      let resetPayload = {
        name: color?.name,
        season: seasonOpt?.value ? seasonOpt : undefined,
        hex: color?.hex,
        rgb_r: colorObj ? colorObj.object().r : undefined,
        rgb_g: colorObj ? colorObj.object().g : undefined,
        rgb_b: colorObj ? colorObj.object().b : undefined,
        note: color?.note,
        year: color?.metadata?.year ? moment(color?.metadata?.year) : null,
      };

      reset(resetPayload);
    }
  }, [color.id]);

  useEffect(() => {
    if (color?.id) {
      const subscription = watch((value, { name, type }) => {
        if (name == "rgb_r" && type == "change") {
          const rgb_g = getValues("rgb_g");
          const rgb_b = getValues("rgb_b");
          try {
            const colorObj = Color({ r: value.rgb_r, g: rgb_g, b: rgb_b });
            setNewColor(colorObj?.hex());
          } catch (err) {}
        } else if (name == "rgb_g" && type == "change") {
          const rgb_r = getValues("rgb_r");
          const rgb_b = getValues("rgb_b");
          try {
            const colorObj = Color({ r: rgb_r, g: value.rgb_g, b: rgb_b });
            setNewColor(colorObj?.hex());
          } catch (err) {}
        } else if (name == "rgb_b" && type == "change") {
          const rgb_r = getValues("rgb_r");
          const rgb_g = getValues("rgb_g");
          try {
            const colorObj = Color({ r: rgb_r, g: rgb_g, b: value.rgb_b });
            setNewColor(colorObj?.hex());
          } catch (err) {}
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [watch, color?.id]);

  const retrieveColor = async () => {
    try {
      const { data } = await api.palette.retrieve(params?.id);
      setColor(data?.color || {});
      setPageNotFound(false);
    } catch (err) {
      toast.error(`Retrieve color failed!`);
      console.log(err);
      setPageNotFound(true);
    }
  };

  const handleUpdateColor = async (data) => {
    try {
      Color(newColor);
    } catch (err) {
      return toast.error(`Invalid color!`);
    }

    try {
      let payload = {
        name: data["name"],
        season: data["season"]?.value,
        hex: newColor,
        note: data["note"],
        metadata: {
          ...color?.metadata,
          year: data["year"] ? moment(data["year"]).format("YYYY") : null,
        },
      };

      if (!color.id) {
        return toast.error("Can't find the color!");
      }

      //check if hex code is different, then allow user to double confirm
      if (newColor == color.hex) {
        await api.palette.update(color.id, payload);
        toast.success(`Edit color successfully!`);
        navigate(-1);
      } else {
        setConfirmVisible(true);
      }
    } catch (err) {
      console.log(err);
      toast.error(`Edit color failed!`);
    }
  };

  const handleColorChange = (e) => {
    const hex = e.target.value.toUpperCase();
    setNewColor(hex);
    try {
      //update form value
      const colorObj = Color(hex);
      setValue("hex", colorObj.hex());
      setValue("rgb_r", colorObj.object().r);
      setValue("rgb_g", colorObj.object().g);
      setValue("rgb_b", colorObj.object().b);
    } catch (err) {}
  };

  const handleRefresh = () => {
    retrieveColor();
  };

  const handleCloseConfirm = () => {
    setConfirmVisible(false);
  };

  const handleConfirmEdit = async () => {
    let data = getValues();
    let payload = {
      name: data["name"],
      season: data["season"]?.value || null,
      hex: newColor,
      note: data["note"],
      metadata: {
        ...color?.metadata,
        year: data["year"] ? moment(data["year"]).format("YYYY") : null,
      },
    };
    try {
      await api.palette.update(color.id, payload);
      toast.success(`${data["name"]} has been saved successfully!`);
      handleCloseConfirm();

      navigate(-1);
    } catch (err) {
      console.log(err);
      toast.error(`Color could not be saved!`);
    }
  };

  const handleConfirmDuplicate = async () => {
    let data = getValues();
    let payload = {
      name: "Duplicate of " + data["name"],
      season: data["season"]?.value || null,
      hex: newColor,
      note: data["note"],
      metadata: {
        ...color?.metadata,
        year: data["year"] ? moment(data["year"]).format("YYYY") : null,
      },
    };
    try {
      await api.palette.create(payload);
      toast.success(`${data["name"]} has been saved successfully!`);
      handleCloseConfirm();

      navigate(-1);
    } catch (err) {
      console.log(err);
      toast.error(`Color could not be saved!`);
    }
  };

  if (pageNotFound) {
    return <Error404 />;
  }

  return (
    <SuiBox px={3}>
      <SuiBox sx={{ zIndex: 2 }} display="flex" alignItems="center" gap={1}>
        <SuiButton variant="text" color="dark" onClick={() => navigate(-1)} size="large">
          <ArrowBackIcon fontSize="sm" sx={{ mr: 0.5 }} /> Back
        </SuiButton>
      </SuiBox>{" "}
      <form onSubmit={handleSubmit(handleUpdateColor)}>
        <Grid container spacing={2} sx={{ px: 3 }}>
          <Grid item xs={12} sm={12} md={5}>
            <Card sx={{ p: 3 }}>
              <SuiBox ml={0.5} mb={2}>
                <SuiTypography variant="h6" fontWeight="bold" textTransform="capitalize">
                  Color Swatch
                </SuiTypography>
              </SuiBox>
              <SuiBox px={4} display="flex" justifyContent="center">
                <input
                  type="color"
                  value={newColor}
                  onChange={handleColorChange}
                  style={{
                    width: 260,
                    height: 320,
                    cursor: "pointer",
                  }}
                />
              </SuiBox>
            </Card>
          </Grid>

          <Grid item xs={12} sm={12} md={7}>
            <Card sx={{ p: 3 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        required
                        textTransform="capitalize"
                      >
                        Color Name
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput
                      placeholder="Color Name"
                      required
                      inputProps={{ maxLength: 200 }}
                      {...register("name", { required: "A color name is required!" })}
                    />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Color HEX
                      </SuiTypography>
                    </SuiBox>
                    <Controller
                      name="hex"
                      control={control}
                      render={({ field }) => (
                        <SuiInput
                          {...field}
                          value={newColor}
                          onChange={(e) => {
                            let value = e.target.value.toUpperCase();
                            value = value.replace(/[^\dA-F]/g, "");
                            if (!value.startsWith("#")) {
                              value = "#" + value;
                            }
                            try {
                              const colorObj = Color(value);
                              setValue("rgb_r", colorObj.object().r);
                              setValue("rgb_g", colorObj.object().g);
                              setValue("rgb_b", colorObj.object().b);
                            } catch (err) {}
                            setNewColor(value);
                          }}
                          inputProps={{ maxLength: 7 }}
                        />
                      )}
                    />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Color RGB
                      </SuiTypography>
                    </SuiBox>
                    <SuiBox display="flex" justifyContent="flex-start" gap={1}>
                      <SuiInput
                        placeholder="R"
                        {...register("rgb_r")}
                        inputProps={{ type: "number", max: 255, min: 0, step: 1 }}
                      />
                      <SuiInput
                        placeholder="G"
                        {...register("rgb_g")}
                        inputProps={{ type: "number", max: 255, min: 0, step: 1 }}
                      />
                      <SuiInput
                        placeholder="B"
                        {...register("rgb_b")}
                        inputProps={{ type: "number", max: 255, min: 0, step: 1 }}
                      />
                    </SuiBox>
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Year
                      </SuiTypography>
                    </SuiBox>
                    <Controller
                      name="year"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          picker="year"
                          placeholder="Year"
                          style={{
                            height: 40,
                            width: "100%",
                            borderRadius: "8px",
                            border: "1px solid #d2d6da",
                          }}
                          popupStyle={{
                            maxHeight: 400,

                            overflow: "auto",
                            zIndex: 9999,
                          }}
                        />
                      )}
                    />
                  </SuiBox>
                </Grid>{" "}
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Season
                      </SuiTypography>
                    </SuiBox>
                    <Controller
                      name="season"
                      control={control}
                      render={({ field }) => (
                        <SuiSelect
                          {...field}
                          options={seasonOptions}
                          placeholder="Season"
                          backspaceRemovesValue
                          isClearable
                          isCreatable
                        />
                      )}
                    />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Description
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput
                      placeholder="Add a new event to your Calendar"
                      rows={4}
                      multiline={true}
                      fullWidth={true}
                      inputProps={{ maxLength: 2000 }}
                      {...register(`note`)}
                    />
                  </SuiBox>
                </Grid>
              </Grid>{" "}
            </Card>
          </Grid>
        </Grid>
        <SuiBox pt={3} px={3} display="flex" justifyContent="end">
          <SuiButton size="medium" variant="gradient" color="dark" type="submit">
            Save
          </SuiButton>
        </SuiBox>
      </form>
      {confirmVisible ? (
        <Dialog
          open={confirmVisible}
          onClose={handleCloseConfirm}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Confirm Edit"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              The related color in style will be updated if you click "Edit".
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <SuiBox display="flex" justifyContent="space-between" width="100%">
              <SuiButton onClick={handleConfirmDuplicate} color="dark" variant="gradient">
                Duplicate
              </SuiButton>
              <SuiBox display="flex" gap={1}>
                <SuiButton onClick={handleCloseConfirm}>Cancel</SuiButton>

                <SuiButton onClick={handleConfirmEdit} autoFocus color="dark" variant="gradient">
                  Edit
                </SuiButton>
              </SuiBox>
            </SuiBox>
          </DialogActions>
        </Dialog>
      ) : null}
    </SuiBox>
  );
};

export default ColorDetail;
